import { faUserShield } from "@fortawesome/free-solid-svg-icons";
import React from "react";
import LegalPageLayout from "../Components/LegalPageLayout";

function PrivacyMM() {
  return (
    <LegalPageLayout
      titleId="privacy-mm-title"
      icon={faUserShield}
      title="Privacy Policy (MileSoft Meds)"
      description={
        <p>
          This page explains how MileSoft Meds handles your personal and health
          information.
        </p>
      }
    >
      <p className="effective-date">Effective Date: August 5, 2026</p>
      <p>
        MileSoft Meds is a medication management application developed and
        operated by MileSoft. It helps users manage medications, schedule
        reminders, and track medication intake. MileSoft is the data controller
        for personal information processed through MileSoft Meds. For privacy
        requests, data protection officer (DPO) requests, and controller contact
        purposes, email us at{" "}
        <a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a>.
      </p>

      <h5>Information We Collect</h5>
      <p>MileSoft Meds may collect or process the following information:</p>
      <ul>
        <li>Name, email address, profile information, and user ID</li>
        <li>Medication names, dosages, schedules, and instructions</li>
        <li>Reminder times</li>
        <li>Dose records, including taken, skipped, or missed doses</li>
        <li>Basic medical history that you choose to provide</li>
        <li>
          Device ID, device type, operating system, app version, app activity,
          crash logs, diagnostics, error logs, and analytics where enabled
        </li>
      </ul>
      <p>
        We treat medication and medical-history information as sensitive
        personal information.
      </p>

      <h5>How We Use Information and Legal Basis</h5>
      <p>We use your information to:</p>
      <ul>
        <li>Create and manage your account</li>
        <li>Store and display your medications</li>
        <li>Schedule reminders</li>
        <li>Record medication intake and adherence</li>
        <li>Synchronize data across devices</li>
        <li>Provide support</li>
        <li>Improve app security, reliability, and performance</li>
      </ul>
      <p>
        Our legal basis for processing may include performance of a contract or
        requested service, consent, legitimate interests in operating and
        securing the app, legal obligations, and processing requested by you.
      </p>

      <h5>Storage and Service Providers</h5>
      <p>
        MileSoft Meds uses Google Firebase services, including authentication,
        cloud storage, notifications, and analytics. Some information may also
        be stored on your device so reminders and existing schedules can
        continue working offline.
      </p>
      <p>
        Your information may be processed or stored in countries other than your
        own, where data protection laws may differ from those in your country.
      </p>

      <h5>Information Sharing, Sales, and Advertising</h5>
      <p>
        MileSoft does not sell your personal or health information or use it for
        advertising. We do not share medication or health information with
        advertisers.
      </p>
      <p>
        We may share limited information with service providers, including
        Google Firebase, where necessary to operate MileSoft Meds. We may also
        disclose information when required by law, to respond to lawful
        requests, to protect users, MileSoft, or the public, or during a
        business transfer.
      </p>
      <p>
        You may opt out of any sale, sharing, or targeted advertising use of
        personal information by emailing{" "}
        <a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a>. We
        will honor applicable opt-out rights even though we do not sell personal
        information or use it for targeted advertising.
      </p>

      <h5>Data Security</h5>
      <p>
        We use reasonable technical and organizational safeguards, including
        encrypted connections, authentication controls, Firebase security rules,
        and cloud security protections. No electronic system can guarantee
        complete security. You are responsible for protecting your device and
        account credentials.
      </p>

      <h5>Data Retention and Deletion</h5>
      <p>
        We retain medication and account information while your account is
        active or until you delete it. Adherence records may be retained for at
        least 12 months to support historical reporting.
      </p>
      <p>
        You may delete individual medication records or request deletion of your
        account through{" "}
        <strong>
          Settings &gt; Delete account &gt; Request account deletion
        </strong>
        . You may also request deletion by emailing{" "}
        <a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a> or
        through our <a href="/contacts">contact page</a>. Please use the email
        address linked to your account so we can identify it.
      </p>
      <p>
        Associated account data will normally be deleted from active systems
        within 30 days unless limited retention is required or permitted for
        legal compliance, security, fraud prevention, dispute resolution, or
        backups. Deleting the app does not automatically delete your account.
      </p>

      <h5>Your Privacy Rights</h5>
      <p>
        Depending on where you live and applicable law, you may have the right
        to request access to your personal information, correct inaccurate
        information, request deletion, receive a portable copy, object to or
        restrict certain processing, withdraw consent where processing is based
        on consent, and know whether your information is sold or shared.
      </p>
      <p>
        To make a privacy rights request, contact{" "}
        <a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a>.
      </p>

      <h5>Children's Privacy</h5>
      <p>
        MileSoft Meds is not designed specifically for children under 13. A
        parent or guardian should supervise use by a child. If we learn that a
        child has provided personal information without appropriate consent, we
        will take reasonable steps to delete it.
      </p>

      <h5>Medical Disclaimer</h5>
      <p>
        MileSoft Meds is a medication organization and reminder tool. It does
        not provide medical advice, diagnosis, or treatment.
      </p>
      <p>
        Always follow instructions from your doctor, pharmacist, or other
        qualified healthcare professional. Do not change or stop medication
        based only on information in the app. Notifications may be delayed by
        device settings, battery restrictions, or technical problems. Do not
        rely on MileSoft Meds for emergency assistance.
      </p>

      <h5>Changes to This Privacy Policy</h5>
      <p>
        MileSoft may update this Privacy Policy when the app, our services, or
        legal requirements change. When we update it, we will revise the
        effective date and may notify users through the app, email, or another
        reasonable method.
      </p>

      <h5>Contact Us</h5>
      <p>
        <strong>Company and App Provider:</strong> MileSoft
        <br />
        <strong>App:</strong> MileSoft Meds
        <br />
        <strong>Controller and DPO Contact:</strong>{" "}
        <a href="mailto:privacy@milesoft.africa">privacy@milesoft.africa</a>
        <br />
        <strong>Location:</strong> Malawi
        <br />
        <strong>Website:</strong>{" "}
        <a href="https://milesoft.africa">milesoft.africa</a>
      </p>
    </LegalPageLayout>
  );
}

export default PrivacyMM;
